import React, { useLayoutEffect, useState } from 'react';
import { Router } from 'react-router-dom';
import { actions as actionsFilterChoice } from '@perxis-auto/instock-sdk/lib/features/filterChoice/model';
import { useAppDispatch, useAppSelector } from '@perxis-auto/instock-sdk/lib/shared/lib/store/hooks';
import { NavigationService } from '@perxis-auto/instock-sdk/lib/shared/services/navigation';
import { selectors as configSelectors } from '@perxis-auto/instock-sdk/lib/shared/store/config';
import { actions as installationActions } from '@perxis-auto/instock-sdk/lib/shared/store/installation/model';
import {
  actions as paginationActions,
  selectors as paginationSelectors,
} from '@perxis-auto/instock-sdk/lib/shared/store/pagination/model';
import {
  HistoryLocationActionEnum,
  HistoryStateType,
  NavigationRouterTypeEnum,
} from '@perxis-auto/instock-sdk/lib/shared/types';
import { Action as HistoryAction, Update as UpdateHistoryState } from 'history';

import { PAGES } from '@/shared/config/pages';
import { getCurrentPage } from '@/shared/lib/router';

import { RouterProviderProps } from './RouterProvider.types';

/**
 * Провайдер для роутера с использованием NavigationService
 * @param children
 */
export function RouterProvider({ children }: RouterProviderProps) {
  const dispatch = useAppDispatch();

  const navigation = NavigationService.getInstance();

  const offset = useAppSelector(paginationSelectors.selectOffset);
  const config = useAppSelector(configSelectors.selectSlice);

  const [state, setState] = useState<HistoryStateType>(() => {
    // https://github.com/remix-run/react-router/issues/8638
    if (config.navigation?.type === NavigationRouterTypeEnum.Hash) {
      const match = window?.location?.hash.match(/#\w+/gi);

      if (match && match.length) {
        navigation.replace(PAGES.Cars);
      }
    }

    const currentPage = getCurrentPage(navigation.location);
    dispatch(installationActions.setCurrentPage(currentPage));

    return {
      location: navigation.location,
      action: navigation.action,
    } as HistoryStateType;
  });

  // Подписка на изменение навигации
  useLayoutEffect(() => {
    return navigation.listen((newState: UpdateHistoryState) => {
      const { type } = config.navigation || {};
      const isSkip = type === NavigationRouterTypeEnum.Hash && newState.location.pathname?.[0] !== '/';

      if (!isSkip) {
        // Если путь не изменился, то используем
        // состояние lastPage из старого состояния
        const isPathnameMatches = state.location.pathname === newState.location.pathname;

        const currentPage = getCurrentPage(newState.location);
        const lastPage = isPathnameMatches ? state.location.state?.lastPage : getCurrentPage(state.location);

        const newStateWithLocationState: HistoryStateType = {
          ...newState,
          location: {
            ...newState.location,
            state: {
              ...(newState.location.state || {}),
              currentPage,
              lastPage,
              lastPathname: state.location.pathname,
            },
          },
        };

        const isPopAction = newStateWithLocationState.action === HistoryAction.Pop;

        const { action: customAction } = newStateWithLocationState.location.state || {};
        const isCustomFiltersAction =
          !!customAction &&
          [
            HistoryLocationActionEnum.SetFilters,
            HistoryLocationActionEnum.ResetFilters,
            HistoryLocationActionEnum.SetOffset,
          ].includes(customAction);

        setState(newStateWithLocationState);

        if (isPopAction || (!isPathnameMatches && !isCustomFiltersAction)) {
          dispatch(installationActions.setPageReady(false));
          dispatch(installationActions.setCurrentPage(currentPage));
        }

        /**
         * Установка фильтров в query params для определенных страниц
         */
        if (currentPage === PAGES.Models || currentPage === PAGES.Cars) {
          switch (newStateWithLocationState.action) {
            case HistoryAction.Replace: {
              if (!isCustomFiltersAction) {
                dispatch(actionsFilterChoice.setValues({}));
              }
              break;
            }
            default: {
              dispatch(actionsFilterChoice.setValues({}));
              if (currentPage === PAGES.Cars) {
                dispatch(paginationActions.setOffset(offset));
              }
              break;
            }
          }
        }
      }
    });
  }, [dispatch, state, offset, config.navigation, navigation]);

  return (
    <Router navigationType={state.action} location={state.location} navigator={navigation.history}>
      {children}
    </Router>
  );
}
